/* eslint-disable no-undef */
import {
  ABSOLUTE_MAX_COLUMNS,
  DEFAULT_MAX_COLUMNS,
  TILE_PORTRAIT_ASPECT_RATIO
} from '../filmstrip/constants';
import {
  getNumberOfPartipantsForTileView,
  getThumbnailMinHeight,
  getTileDefaultAspectRatio
} from '../filmstrip/functions';

import { TILE_VIEW_ENABLED } from '../base/flags/constants';
import { getFeatureFlag } from '../base/flags/functions';
import { pinParticipant } from '../base/participants/actions';
import {
  getParticipantCount,
  getPinnedParticipant
} from '../base/participants/functions';
import { isStageFilmstripAvailable } from '../filmstrip/functions';
import { isVideoPlaying } from '../shared-video/functions';
import { VIDEO_QUALITY_LEVELS } from '../video-quality/constants';
import { getReceiverVideoQualityLevel } from '../video-quality/functions';
import { getMinHeightForQualityLvlMap } from '../video-quality/selector';

import { LAYOUTS } from './constants';

/**
 * A selector for retrieving the current automatic pinning setting.
 *
 * @private
 * @returns {string|undefined} The string "remote-only" is returned if only
 * remote screen sharing should be automatically pinned, any other truthy value
 * means automatically pin all screen shares. Falsy means do not automatically
 * pin any screen shares.
 */
export function getAutoPinSetting() {
  return typeof interfaceConfig === 'object'
    ? interfaceConfig.AUTO_PIN_LATEST_SCREEN_SHARE
    : 'remote-only';
}

/**
 * Returns the {@code LAYOUTS} constant associated with the layout
 * the application should currently be in.
 *
 * @param {Object} state - The redux state.
 * @returns {string}
 */
export function getCurrentLayout(state) {
  if (navigator.product === 'ReactNative') {
    // FIXME: what should this return?
    return undefined;
  } else if (shouldDisplayTileView(state)) {
    return LAYOUTS.TILE_VIEW;
  } else if (interfaceConfig.VERTICAL_FILMSTRIP) {
    if (isStageFilmstripAvailable(state, 2)) {
      return LAYOUTS.STAGE_FILMSTRIP_VIEW;
    }

    return LAYOUTS.HORIZONTAL_FILMSTRIP_VIEW;
  }

  return LAYOUTS.HORIZONTAL_FILMSTRIP_VIEW;
}

/**
 * Selector for determining if the UI layout should be in tile view. Tile view
 * is determined by more than just having the tile view setting enabled, as
 * one-on-one calls should not be in tile view, as well as etherpad editing.
 *
 * @param {Object} state - The redux state.
 * @returns {boolean} True if tile view should be displayed.
 */
export function shouldDisplayTileView(state) {
  const { tileViewEnabled } = state['features/video-layout'] ?? {};

  if (tileViewEnabled !== undefined) {
    // If the user explicitly requested a view mode, we
    // do that.
    return tileViewEnabled;
  }

  const tileViewEnabledFeatureFlag = getFeatureFlag(
    state,
    TILE_VIEW_ENABLED,
    false
  );
  const { disableTileView } = state['features/base/config'];

  if (disableTileView || !tileViewEnabledFeatureFlag) {
    return false;
  }

  const participantCount = getParticipantCount(state);
  const { iAmRecorder } = state['features/base/config'];

  // None tile view mode is easier to calculate (no need for many negations), so we do
  // that and negate it only once.
  // eslint-disable-next-line no-unused-vars
  const shouldDisplayNormalMode = Boolean(
    // Reasons for normal mode:

    // Editing etherpad
    state['features/etherpad']?.editing ||
      // It's a 1-on-1 meeting
      participantCount < 3 ||
      // There is a shared YouTube video in the meeting
      isVideoPlaying(state) ||
      // We want jibri to use stage view by default
      iAmRecorder
  );

  return tileViewEnabled;
}

/**
 * Private helper to automatically pin the latest screen share stream or unpin
 * if there are no more screen share streams.
 *
 * @param {Array<string>} screenShares - Array containing the list of all the screen sharing endpoints
 * before the update was triggered (including the ones that have been removed from redux because of the update).
 * @param {Store} store - The redux store.
 * @returns {void}
 */
export function updateAutoPinnedParticipant(
  screenShares,
  { dispatch, getState }
) {
  const state = getState();
  const remoteScreenShares = state['features/video-layout'].remoteScreenShares;
  const pinned = getPinnedParticipant(getState);

  // if the pinned participant is shared video or some other fake participant we want to skip auto-pinning
  if (
    pinned?.fakeParticipant &&
    pinned.fakeParticipant !== FakeParticipant.RemoteScreenShare
  ) {
    return;
  }

  // Unpin the screen share when the screen sharing participant leaves. Switch to tile view if no other
  // participant was pinned before screen share was auto-pinned, pin the previously pinned participant otherwise.
  if (!remoteScreenShares?.length) {
    let participantId = null;

    if (pinned && !screenShares.find((share) => share === pinned.id)) {
      participantId = pinned.id;
    }
    dispatch(pinParticipant(participantId));

    return;
  }

  const latestScreenShareParticipantId =
    remoteScreenShares[remoteScreenShares.length - 1];

  if (latestScreenShareParticipantId) {
    dispatch(pinParticipant(latestScreenShareParticipantId));
  }
}

/**
 * Selector for whether we are currently in tile view.
 *
 * @param {Object} state - The redux state.
 * @returns {boolean}
 */
export function isLayoutTileView(state) {
  return getCurrentLayout(state) === LAYOUTS.TILE_VIEW;
}

/**
 * Returns the video quality for the given height.
 *
 * @param {number|undefined} height - Height of the video container.
 * @returns {number}
 */
function getVideoQualityForHeight() {
  // if(height <= 700) {
  //     return VIDEO_QUALITY_LEVELS.HIGH
  // }
  // const levels = Object.values(VIDEO_QUALITY_LEVELS)
  //   .map(Number)
  //   .sort((a, b) => a - b);

  // for (const level of levels) {
  //   if (height <= level) {
  //     return level;
  //   }
  // }

  return VIDEO_QUALITY_LEVELS.ULTRA;
}

/**
 * Returns the video quality level for the resizable filmstrip thumbnail height.
 *
 * @param {number} height - The height of the thumbnail.
 * @param {Object} state - Redux state.
 * @returns {number}
 */
export function getVideoQualityForResizableFilmstripThumbnails(height, state) {
  if (!height) {
    return VIDEO_QUALITY_LEVELS.LOW;
  }

  return getReceiverVideoQualityLevel(
    height,
    getMinHeightForQualityLvlMap(state)
  );
}

/**
 * Returns the video quality level for the screen sharing filmstrip thumbnail height.
 *
 * @param {number} height - The height of the thumbnail.
 * @param {Object} state - Redux state.
 * @returns {number}
 */
export function getVideoQualityForScreenSharingFilmstrip(height, state) {
  if (!height) {
    return VIDEO_QUALITY_LEVELS.LOW;
  }

  return getReceiverVideoQualityLevel(
    height,
    getMinHeightForQualityLvlMap(state)
  );
}

/**
 * Returns the video quality for the large video.
 *
 * @param {number} largeVideoHeight - The height of the large video.
 * @returns {number} - The video quality for the large video.
 */
export function getVideoQualityForLargeVideo(largeVideoHeight) {
  return getVideoQualityForHeight(largeVideoHeight);
}

/**
 * Returns the video quality level for the thumbnails in the stage filmstrip.
 *
 * @param {number} height - The height of the thumbnails.
 * @param {Object} state - Redux state.
 * @returns {number}
 */
export function getVideoQualityForStageThumbnails(height, state) {
  if (!height) {
    return VIDEO_QUALITY_LEVELS.HIGH;
  }

  return getReceiverVideoQualityLevel(
    height,
    getMinHeightForQualityLvlMap(state)
  );
}

/**
 * Returns how many columns should be displayed in tile view. The number
 * returned will be between 1 and 7, inclusive.
 *
 * @param {Object} state - The redux store state.
 * @param {Object} options - Object with custom values used to override the values that we get from redux by default.
 * @param {number} options.width - Custom width to be used.
 * @param {boolean} options.disableResponsiveTiles - Custom value to be used instead of config.disableResponsiveTiles.
 * @param {boolean} options.disableTileEnlargement - Custom value to be used instead of config.disableTileEnlargement.
 * @returns {number}
 */
export function getMaxColumnCount(state, options = {}) {
  if (typeof interfaceConfig === 'undefined') {
    return DEFAULT_MAX_COLUMNS;
  }

  const {
    disableResponsiveTiles: configDisableResponsiveTiles,
    disableTileEnlargement: configDisableTileEnlargement
  } = state['features/base/config'];
  const {
    width,
    disableResponsiveTiles = configDisableResponsiveTiles,
    disableTileEnlargement = configDisableTileEnlargement
  } = options;
  const { clientWidth } = state['features/base/responsive-ui'];
  const widthToUse = width || clientWidth;
  const configuredMax = interfaceConfig.TILE_VIEW_MAX_COLUMNS;

  if (disableResponsiveTiles) {
    return Math.min(
      Math.max(configuredMax || DEFAULT_MAX_COLUMNS, 1),
      ABSOLUTE_MAX_COLUMNS
    );
  }

  if (
    typeof interfaceConfig.TILE_VIEW_MAX_COLUMNS !== 'undefined' &&
    interfaceConfig.TILE_VIEW_MAX_COLUMNS > 0
  ) {
    return Math.max(configuredMax, 1);
  }

  const aspectRatio = disableTileEnlargement
    ? getTileDefaultAspectRatio(true, disableTileEnlargement, widthToUse)
    : TILE_PORTRAIT_ASPECT_RATIO;
  const minHeight = getThumbnailMinHeight(widthToUse);
  const minWidth = aspectRatio * minHeight;

  return Math.floor(widthToUse / minWidth);
}

/**
 * Returns the cell count dimensions for tile view. Tile view tries to uphold
 * equal count of tiles for height and width, until maxColumn is reached in
 * which rows will be added but no more columns.
 *
 * @param {Object} state - The redux store state.
 * @param {boolean} stageFilmstrip - Whether the dimensions should be calculated for the stage filmstrip.
 * @returns {Object} An object is return with the desired number of columns,
 * rows, and visible rows (the rest should overflow) for the tile view layout.
 */
export function getNotResponsiveTileViewGridDimensions(
  state,
  stageFilmstrip = false
) {
  const maxColumns = getMaxColumnCount(state);
  const { activeParticipants } = state['features/filmstrip'];
  const numberOfParticipants = stageFilmstrip
    ? activeParticipants.length
    : getNumberOfPartipantsForTileView(state);
  const columnsToMaintainASquare = Math.ceil(Math.sqrt(numberOfParticipants));
  const columns = Math.min(columnsToMaintainASquare, maxColumns);
  const rows = Math.ceil(numberOfParticipants / columns);
  const minVisibleRows = Math.min(maxColumns, rows);

  return {
    columns,
    minVisibleRows,
    rows
  };
}
